import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { DateTime } from "luxon";
import { Subscription } from "rxjs";
import { BidRoundStatus, BidStatus } from "../../../interfaces/enums";
import { BidResponse, Trade } from "../../../interfaces/responses/models";
import { BidRoundService } from "../../../services/bid-round.service";
import { BidService } from "../../../services/bids.service";
import { ScreenSizeService } from "../../../services/screen-size.service";
import { WebsocketService } from "../../../services/websocket.service";

@Component({
  selector: "app-allocation",
  templateUrl: "./allocation.component.html",
  styleUrls: ["./allocation.component.css"],
})
export class AllocationComponent implements OnInit, OnDestroy {
  public bidAllocation: Trade[] = [];
  bids: BidResponse[] = [];
  activeBid: any;
  acceptedBidId: any | null = null;
  isAllocated: boolean = false;
  error: string | null = null;
  bidRound: any = { strike_price: 0, transaction_type: "Sell" };
  strikePrice: number = -1;
  isMobile: boolean = false;

  constructor(
    private bidService: BidService,
    private ws: WebsocketService,
    private route: Router,
    private activatedRoute: ActivatedRoute,
    private bidRoundService: BidRoundService,
    private screenSizeService: ScreenSizeService
  ) {}

  private wsSubscription: Subscription | undefined;
  private bidRoundId: string | undefined;

  ngOnInit(): void {
    this.screenSizeService.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });
    const bidId = this.activatedRoute.snapshot.paramMap.get("id");
    if (bidId) {
      this.getBidById(bidId);
    } else {
      console.log(bidId);
      this.getLastAcceptedBidId();
    }
  }
  goHome() {
    this.route.navigate(["/market"]);
  }
  getBidById(bidId: string) {
    this.bidService.getBidById(bidId).subscribe(
      (data) => {
        this.activeBid = data;
        console.log("active_bid: ", this.activeBid);
        this.getBidRound();
        console.log("Bid-Detail", this.activeBid);
        if (this.activeBid.status === BidStatus.Accepted) {
          this.subscribeToNotifications();
        }
      },
      (error) => {
        console.log("Error", error);
      }
    );
  }
  get isFixRateBid(): boolean {
    return this.activeBid.bid_type === "Preferential Exchange of Energy";
  }
  parseDate(date: string): DateTime {
    return DateTime.fromFormat(date, "LLLL dd hh:mm a");
  }
  formatTradeTime(date: string): string {
    return this.parseDate(date).toFormat("dd-MMM-yyyy");
  }
  formatTradeTimeYear(date: string): string {
    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      throw new Error("Invalid date format");
    }
    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = parsedDate.toLocaleString("default", { month: "short" });
    const year = parsedDate.getFullYear();

    return `${day}-${month}-${year}`;
  }
  getLastAcceptedBidId() {
    const filter = { limit: 1 };
    this.bidService.getFilteredBids(filter).subscribe(
      (data) => {
        if (Array.isArray(data.items)) {
          this.bids = data.items;
          let relevantBids = this.bids.filter((x) => {
            return (
              x.status === BidStatus.Accepted || x.status === BidStatus.Rejected
            );
          });
          let sortedItems = relevantBids.sort((a: any, b: any) => {
            return (
              new Date(b.created_on).getTime() -
              new Date(a.created_on).getTime()
            );
          });
          this.activeBid = sortedItems[0];
          if (sortedItems.length === 0) {
            this.route.navigateByUrl("/");
          } else {
            if (this.activeBid.status === BidStatus.Accepted) {
              this.getBidRound();
              this.subscribeToNotifications();
            } else if (this.activeBid.status === BidStatus.Rejected) {
              this.getBidRound();
            }
          }
        }
      },
      (error) => {
        console.log("Error", error);
      }
    );
  }

  getBidAllocation(bid_id: any) {
    console.log("bid Id:" + bid_id);
    this.bidService.allocateBid(bid_id).subscribe(
      (data) => {
        console.log("data", data);
        if (data.length === 0) {
          return;
        }
        this.bidAllocation = data;
      },
      (error) => {
        console.log("Error");
      }
    );
  }

  getBidRound() {
    if (this.activeBid.bid_round)
      this.bidRoundService.getBidRound(this.activeBid.bid_round).subscribe(
        (data) => {
          if (
            (data.status == BidRoundStatus.Completed ||
              data.status == BidRoundStatus.Traded) &&
            this.activeBid.status === BidStatus.Accepted
          ) {
            this.getBidAllocation(this.activeBid.id);
            this.isAllocated = true;
          }
          this.bidRound = data;
          this.strikePrice = parseInt(data.strike_price);
          console.log(this.activeBid.bid_round);
        },
        (error) => {
          console.error("Error loading bid rounds:", error);
          this.error = "Error loading bid rounds: " + error.message;
        }
      );
  }

  subscribeToNotifications() {
    // Clean up any existing subscription first
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = undefined;
    }

    if (!this.activeBid?.bid_round) {
      console.warn("Cannot connect to WebSocket: missing bid round ID");
      return;
    }

    this.bidRoundId = this.activeBid.bid_round;
    console.log(`Connecting to WebSocket for bid round: ${this.bidRoundId}`);

    // Connect using the specific bid round method
    this.ws.connectBidRound(this.bidRoundId);

    // Subscribe to the bid-round specific message stream
    this.wsSubscription = this.ws.bidRoundMessages$.subscribe({
      next: (message: any) => {
        console.log("WebSocket message received (Bid Round):", message);
        // Process message specific to bid round events
        if (
          message.type === "completed_bid_round" ||
          message.type === "allocation"
        ) {
          // Assuming 'allocation' might be a type
          this.getBidRound(); // Refresh bid round data, which might trigger allocation fetch
        }
        // Add other message type handling as needed for this component
      },
      error: (error) => {
        console.error("WebSocket error (Bid Round):", error);
      },
      complete: () => {
        // This might not be called reliably if the connection closes unexpectedly,
        // but good practice to log if it does complete gracefully.
        console.log("WebSocket connection closed gracefully (Bid Round)");
      },
    });
  }

  navigateTo(path: string) {
    this.route.navigate([path]);
  }

  ngOnDestroy(): void {
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = undefined;
    }
    this.ws.closeBidRoundConnection(); // Close the specific bid round connection
    this.bidRoundId = undefined;
  }
}
